import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import Bird3D from '@/ui-assets/images/bird-love.webp';
import { trackEvent } from '@/utils/analytics';
import { getRuntimeConfig } from '@/utils/config';

import styles from './index.module.scss';

type ApiState = 'idle' | 'loading';

const RecoverAccount = () => {
  const { t } = useTranslation();
  const [apiState, setApiState] = useState<ApiState>('idle');
  const [isRecovered, setIsRecovered] = useState<boolean>(false);

  const navigate = useNavigate();
  const [params] = useSearchParams();

  async function onConfirm() {
    trackEvent(
      'recover_account_confirm_button',
      'clicked',
      { eventId: 'recover_account_confirm_button_clicked' }
    );
    const config = getRuntimeConfig();
    const userId = params.get('id');

    if (!userId) {
      return;
    }

    setApiState('loading');

    try {
      const response = await fetch(`${config.VITE_ONE_MEMBER_API_ENDPOINT}/api/users/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setIsRecovered(true);
    } catch (error) {
      console.error('Error during fetch:', error);
    } finally {
      setApiState('idle');
    }
  }

  return (
    <div className={styles.imageLayoutContainer}>
      <img src={Bird3D} width={160} alt="" />
      <div className={styles.textContainer}>
        {isRecovered ? (
          <>
            <h1>{t('other.recovered_title')}</h1>
            <p>{t('other.recovered_description')}</p>
          </>
        ) : (
          <>
            <h1>{t('other.welcome_back_title')}</h1>
            <p>{t('other.welcome_back_description')}</p>
          </>
        )}
      </div>
      {isRecovered ? (
        <div className={styles.buttonContainer}>
          <Button
            title={t('action.sign_in')}
            htmlType="button"
            size="large"
            className={styles.confirmButton}
            onClick={() => {
              trackEvent(
                'recover_account_sign_in_button',
                'clicked',
                { eventId: 'recover_account_sign_in_button_clicked' },
                {}
              );
              navigate('/sign-in');
            }}
          />
        </div>
      ) : (
        <div className={styles.buttonContainer}>
          <Button
            title={t('action.recover')}
            htmlType="button"
            size="large"
            className={styles.confirmButton}
            isLoading={apiState === 'loading'}
            onClick={onConfirm}
          />
          <Button
            title={t('action.cancel')}
            type="secondary"
            htmlType="button"
            className={styles.cancelButton}
            onClick={() => {
              trackEvent(
                'recover_account_cancel_button',
                'clicked',
                { eventId: 'recover_account_cancel_button_clicked' }
              );
              navigate('/sign-in');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RecoverAccount;
